// Mirrors Skatteetaten's reference validator:
// https://skatteetaten.github.io/folkeregisteret-api-dokumentasjon/dokumenter/foedselsEllerDNummerValidator.js
const FIRST_CHECK_DIGIT_WEIGHTS = [3, 7, 6, 1, 8, 9, 4, 5, 2] as const;
const SECOND_CHECK_DIGIT_WEIGHTS = [5, 4, 3, 2, 7, 6, 5, 4, 3, 2] as const;
// The original scheme from 1964 always give remainder 0 for check digits; upcoming 2032 year change will also accept the remainder numbers 0-3.
const VALID_FIRST_CHECK_REMAINDERS = [0, 1, 2, 3];
// D-numbers, issued to people without a fødselsnummer, add 40 to the day.
const D_NUMBER_DAY_OFFSET = 40;

/** Checks a Norwegian SSN (fødselsnummer / D-number, 1964 and 2032 types). */
export function isValidNorwegianSsn(ssn: string): boolean {
  // Norwegian fødselsnummer / D-number format: DDMMYY[Individual Number][Check Digit 1][Check Digit 2]
  const ssnRegex = /^(\d{2})(\d{2})(\d{2})\d{5}$/;
  const match = ssn.match(ssnRegex);

  if (!match) {
    return false;
  }

  const [, day, month, year] = match;

  const dayNum = parseInt(day, 10);
  const birthDay = dayNum > D_NUMBER_DAY_OFFSET ? dayNum - D_NUMBER_DAY_OFFSET : dayNum;

  if (!isCalendarDate(birthDay, parseInt(month, 10), parseInt(year, 10))) {
    return false;
  }

  const digits = [...ssn].map((digit) => parseInt(digit, 10));
  const firstCheckRemainder = calculateWeightedSum(digits, FIRST_CHECK_DIGIT_WEIGHTS) % 11;
  const secondCheckRemainder = calculateWeightedSum(digits, SECOND_CHECK_DIGIT_WEIGHTS) % 11;

  return VALID_FIRST_CHECK_REMAINDERS.includes(firstCheckRemainder) && secondCheckRemainder === 0;
}

/** The century can't be derived from 2032-type numbers, so like Skatteetaten we assume 20xx for leap years. */
function isCalendarDate(day: number, month: number, twoDigitYear: number): boolean {
  const date = new Date(2000 + twoDigitYear, month - 1, day);
  return date.getMonth() === month - 1 && date.getDate() === day;
}

/** Sums the digits times their weights, plus the check digit that follows them with weight 1. */
function calculateWeightedSum(digits: number[], weights: readonly number[]): number {
  let sum = digits[weights.length];
  for (const [i, weight] of weights.entries()) {
    sum += digits[i] * weight;
  }
  return sum;
}
